import { useState } from 'react';
import { api, usd } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';

export default function Customers() {
  const [q, setQ] = useState('');
  const rows = useLoad(() => api<any[]>(`/api/customers?q=${encodeURIComponent(q)}`), [q]);
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Customers</h1><p>Loyalty members. Customers earn 1 point per $1 spent; 100 points take $5 off. Club members also get club-only promotions.</p></div>
        <button className="primary" onClick={() => setAdding(true)}>Add customer</button>
      </div>
      <Owing />
      <input style={{ maxWidth: 360 }} placeholder="Search name, phone or email" value={q} onChange={(e) => setQ(e.target.value)} />
      <ErrorNote error={rows.error} />
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Name</th><th>Phone</th><th>Email</th><th>Club</th><th className="n">Points</th><th className="n">Lifetime spend</th></tr></thead>
          <tbody>{rows.data?.map((c) => (
            <tr key={c.id} className="clickable" onClick={() => setOpen(c.id)}>
              <td style={{ fontWeight: 600 }}>{c.name}</td><td>{c.phone ?? '—'}</td><td>{c.email ?? '—'}</td>
              <td>{c.club ? <span className="chip good">Club</span> : ''}</td><td className="n">{c.points}</td><td className="n">{usd(Number(c.lifetime_cents))}</td>
            </tr>))}</tbody>
        </table>
        {rows.data?.length === 0 && <div className="empty">No customers yet.</div>}
      </div></div>
      {adding && <AddCustomer onClose={() => setAdding(false)} onDone={() => { setAdding(false); rows.reload(); }} />}
      {open && <CustomerDetail id={open} onClose={() => { setOpen(null); rows.reload(); }} />}
    </div>
  );
}

function AddCustomer({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ name: '', phone: '', email: '', club: false });
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title="Add customer" onClose={onClose} footer={<button className="primary" disabled={!f.name.trim()} onClick={async () => {
      try { await api('/api/customers', { body: { name: f.name, phone: f.phone || null, email: f.email || null, club: f.club } }); onDone(); } catch (e) { setErr(e); }
    }}>Save</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Name<input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <label>Phone<input inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></label>
      </div>
      <label>Email<input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
      <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)' }}>
        <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={f.club} onChange={(e) => setF({ ...f, club: e.target.checked })} /> Club member
      </label>
    </Modal>
  );
}

function CustomerDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const c = useLoad(() => api<any>(`/api/customers/${id}`), [id]);
  const [delta, setDelta] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<unknown>(null);
  async function run(fn: () => Promise<unknown>) { setErr(null); try { await fn(); c.reload(); } catch (e) { setErr(e); } }
  const d = c.data;
  return (
    <Modal title={d?.name ?? 'Customer'} onClose={onClose} wide>
      <ErrorNote error={err ?? c.error} />
      {d && (
        <div className="stack">
          <p>{d.phone ?? 'No phone'} · {d.email ?? 'No email'} · <b>{d.points} points</b> · lifetime {usd(Number(d.lifetime_cents))}</p>
          <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)' }}>
            <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={d.club} onChange={(e) => run(() => api(`/api/customers/${id}`, { method: 'PATCH', body: { club: e.target.checked } }))} /> Club member
          </label>
          <GroupPicker id={id} groupId={d.group_id} onChange={(gid) => run(() => api(`/api/customers/${id}/group`, { method: 'PUT', body: { groupId: gid } }))} />
          <div className="field-row">
            <label>Adjust points (+ or −)<input inputMode="numeric" value={delta} onChange={(e) => setDelta(e.target.value.replace(/[^\d-]/g, ''))} placeholder="e.g. 50 or -50" /></label>
            <label>Reason<input value={note} onChange={(e) => setNote(e.target.value)} /></label>
            <button disabled={!Number(delta)} onClick={() => run(async () => { await api(`/api/customers/${id}/points`, { body: { delta: Number(delta), note } }); setDelta(''); setNote(''); })}>Apply</button>
          </div>
          <AccountSection id={id} />
          <h3>Points history</h3>
          <table className="grid"><tbody>
            {d.pointsHistory.map((p: any, i: number) => (
              <tr key={i}><td>{new Date(p.created_at).toLocaleString()}</td><td>{p.reason}{p.note ? ` · ${p.note}` : ''}</td><td className="n">{p.delta > 0 ? '+' : ''}{p.delta}</td></tr>))}
          </tbody></table>
          {d.pointsHistory.length === 0 && <div className="empty">No activity yet.</div>}
        </div>
      )}
    </Modal>
  );
}

// A customer's group sets their register pricing (see Groups and prices).
function GroupPicker({ groupId, onChange }: { id: string; groupId: string | null; onChange: (g: string | null) => void }) {
  const groups = useLoad(() => api<any[]>('/api/customer-groups').catch(() => [] as any[]), []);
  if (!groups.data?.length) return null;
  return (
    <label>Customer group
      <select value={groupId ?? ''} onChange={(e) => onChange(e.target.value || null)}>
        <option value="">None</option>
        {groups.data.map((g) => <option key={g.id} value={g.id}>{g.name}{g.discount_pct ? ` · ${g.discount_pct}% off` : ''}{g.price_list_name ? ` · ${g.price_list_name}` : ''}</option>)}
      </select>
    </label>
  );
}

// What customers owe on house accounts.
function Owing() {
  const owing = useLoad(() => api<any[]>('/api/accounts').catch(() => [] as any[]), []);
  const rows = (owing.data ?? []).filter((r) => r.balance_cents > 0);
  if (rows.length === 0) return null;
  return <div className="notice warn">Owed on house accounts: <b>{usd(rows.reduce((s, r) => s + r.balance_cents, 0))}</b> from {rows.length} customer{rows.length === 1 ? '' : 's'}: {rows.slice(0, 5).map((r) => `${r.name} ${usd(r.balance_cents)}`).join(', ')}{rows.length > 5 ? '…' : ''}</div>;
}

// House account: let a trusted customer charge purchases, take their payments, and print a statement.
function AccountSection({ id }: { id: string }) {
  const acct = useLoad(() => api<any>(`/api/customers/${id}/account`), [id]);
  const [limit, setLimit] = useState<string | null>(null);
  const [pay, setPay] = useState({ amount: '', method: 'cash' });
  const [statement, setStatement] = useState<any>(null);
  const [err, setErr] = useState<unknown>(null);
  const a = acct.data;
  if (!a) return <ErrorNote error={acct.error} />;
  const limitText = limit ?? (a.credit_limit_cents == null ? '' : (a.credit_limit_cents / 100).toFixed(2));
  async function save(enabled: boolean) {
    setErr(null);
    try { await api(`/api/customers/${id}/account`, { method: 'PUT', body: { enabled, creditLimitCents: limitText === '' ? null : Math.round(Number(limitText) * 100) } }); setLimit(null); acct.reload(); } catch (e) { setErr(e); }
  }
  async function openStatement() {
    setErr(null);
    const to = new Date().toISOString().slice(0, 10); const from = new Date(Date.now() - 31 * 864e5).toISOString().slice(0, 10);
    try { setStatement(await api(`/api/customers/${id}/statement?from=${from}&to=${to}`)); } catch (e) { setErr(e); }
  }
  return (
    <div className="panel"><div className="panel-head"><h3 className="grow">House account</h3>{a.account_enabled && <span className="chip good">on</span>}</div>
      <div className="panel-body stack">
        <ErrorNote error={err} />
        <div className="field-row">
          <label>Credit limit ($, blank for none)<input className="num-input" inputMode="decimal" value={limitText} onChange={(e) => setLimit(e.target.value.replace(/[^\d.]/g, ''))} /></label>
          <button onClick={() => save(true)}>{a.account_enabled ? 'Save limit' : 'Turn on house account'}</button>
          {a.account_enabled && <button onClick={() => { if (confirm('Turn off charging for this customer? They still owe what is on the account.')) save(false); }}>Turn off</button>}
        </div>
        {a.account_enabled && (
          <>
            <p>Owes <b>{usd(a.balanceCents)}</b>{a.credit_limit_cents != null ? ` of ${usd(a.credit_limit_cents)}` : ''}</p>
            <div className="field-row">
              <label>Payment received ($)<input className="num-input" inputMode="decimal" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value.replace(/[^\d.]/g, '') })} /></label>
              <label>Paid by<select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}><option value="cash">Cash</option><option value="card">Card</option><option value="check">Check</option></select></label>
              <button disabled={!(Number(pay.amount) > 0)} onClick={async () => { setErr(null); try { await api(`/api/customers/${id}/account/payments`, { body: { amountCents: Math.round(Number(pay.amount) * 100), method: pay.method } }); setPay({ ...pay, amount: '' }); acct.reload(); } catch (e) { setErr(e); } }}>Record payment</button>
              <button onClick={openStatement}>Statement</button>
            </div>
          </>
        )}
      </div>
      {statement && (
        <Modal title={`Statement · ${statement.customer.name}`} onClose={() => setStatement(null)} wide footer={<button onClick={() => window.print()}>Print</button>}>
          <p className="faint">{statement.from} to {statement.to}</p>
          <table className="grid"><tbody>
            <tr><td>Balance brought forward</td><td /><td className="n">{usd(statement.openingCents)}</td></tr>
            {statement.rows.map((r: any) => (
              <tr key={r.id}><td>{new Date(r.created_at).toLocaleDateString()}</td>
                <td>{r.kind === 'charge' ? 'Purchase' : r.kind === 'payment' ? `Payment (${r.method ?? ''})` : 'Adjustment'}{r.items?.length ? <div className="faint">{r.items.map((i: any) => `${i.qty} × ${i.brand} ${i.name}`).join(', ')}</div> : null}</td>
                <td className="n">{usd(r.amount_cents)} <span className="faint">→ {usd(r.balance_cents)}</span></td></tr>))}
            <tr><td><b>Balance due</b></td><td /><td className="n"><b>{usd(statement.closingCents)}</b></td></tr>
          </tbody></table>
        </Modal>
      )}
    </div>
  );
}
